/** Account-page hooks for MCP tokens (spec §8.27, §10.9.5). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, unwrap } from "../api/client";
import type { components } from "../api/schema";

export type McpToken = components["schemas"]["TokenOut"];
export type Account = components["schemas"]["AccountOut"];
export type NewToken = components["schemas"]["TokenIn"];

const key = ["account", "tokens"] as const;

export function useAccountTokens() {
  return useQuery({ queryKey: key, queryFn: async () => unwrap<Account>(await api.GET("/api/account/tokens")) });
}

/** 409 carries a code the page translates. */
export class TokenRefusal extends Error {
  constructor(public code: string) {
    super(code);
  }
}

export function useCreateToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: NewToken) => {
      const res = await api.POST("/api/account/tokens", { body });
      if (res.response.status === 409) throw new TokenRefusal(((res.error ?? {}) as { code?: string }).code ?? "refused");
      return unwrap<components["schemas"]["TokenCreated"]>(res);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
}

export function useRevokeToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE("/api/account/tokens/{token_id}", { params: { path: { token_id: id } } });
      if (!res.response.ok) throw new ApiError(res.response.status, res.response.statusText);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
}
